import Cookies from 'js-cookie';

import type { AwsWafConfig } from '../types';
import { checkFeatureGate } from '../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { addBreadcrumb } from '../utilities/analytics/error-reporting';

// The AWS WAF SDK sets the token as a cookie named 'aws-waf-token'.
// See: https://docs.aws.amazon.com/waf/latest/developerguide/waf-tokens-details.html
const AWS_CAPTCHA_TOKEN_COOKIE_NAME = 'aws-waf-token';

/**
 * Returns `true` (and narrows the type) when both required fields are present and non-empty.
 */
export function isValidAwsWafConfig(
  awsWafConfig: AwsWafConfig | null | undefined
): awsWafConfig is AwsWafConfig {
  return !!(awsWafConfig?.integrationUrl && awsWafConfig?.apiKey);
}

/**
 * Optional WAF headers for login requests.
 * `test-captcha` forces a 405 in E2E when the host-headers gate is on.
 * `atl-edge-auth-challenge-rollout` opts the request into the initial AWS Captcha ruleset.
 */
export function getAwsCaptchaWafHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};

  if (checkFeatureGate(AidFrontendFeatureGate.ENABLE_HOST_HEADERS_FOR_AWS_CAPTCHA)) {
    headers['test-captcha'] = 'true';
  }

  if (checkFeatureGate(AidFrontendFeatureGate.AWS_CAPTCHA_INITIAL_RULESET)) {
    headers['atl-edge-auth-challenge-rollout'] = '1';
  }

  return headers;
}

/**
 * Function to get the value from the token cookie, ensuring safety in case the function throws an error.
 *
 * @returns String
 */
export function getAwsCaptchaTokenValue() {
  try {
    const value = Cookies.get(AWS_CAPTCHA_TOKEN_COOKIE_NAME) || '';

    if (!value || typeof value !== 'string') {
      return '';
    }

    return value;
  } catch (e) {
    // Make sure we return a value even if there was an error
    return '';
  }
}

export type OnShowAwsCaptchaModalCallbackProps = {
  /** Function called when the puzzle is successfully completed. */
  onSuccess: () => void;
};

type Opts = {
  awsWafConfig: AwsWafConfig | undefined;
  /**
   * A callback that renders the AWS Captcha puzzle. Used when a 405 is returned from the WAF
   * which indicates the user must complete a captcha puzzle before
   * the request can be retried.
   */
  onShowAwsCaptchaModal: ({ onSuccess }: OnShowAwsCaptchaModalCallbackProps) => void;
  /**
   * What to do when user successfully completes the puzzle.
   */
  retry: () => any;
  /**
   * Adds context to breadcrumb logs
   */
  breadcrumbTag: string;
  /**
   * Anything additional to do when config is not valid
   */
  onInvalidConfig?: () => void;
};

type CaptchaHandling =
  | {
      shouldHandle: true;
      handler: () => void;
    }
  | {
      shouldHandle: false;
    };

export const buildAwsCaptchaHandling = (
  responseStatusCode: number,
  opts: Opts
): CaptchaHandling => {
  const { awsWafConfig, onShowAwsCaptchaModal, retry, onInvalidConfig, breadcrumbTag } = opts;

  // 405 is returned only by AWS Captcha in the situation where the WAF is asking the user to complete a captcha. We also require config to be present — if the gate is on but config hasn't arrived from the backend yet,
  // we can't render the puzzle, so we fall through to normal error handling rather than showing an empty modal.
  const isAwsCaptchaConfigured = isValidAwsWafConfig(awsWafConfig);

  // Whether the AWS Captcha feature is enabled
  const isAwsCaptchaEnabled = checkFeatureGate(AidFrontendFeatureGate.ENABLE_AWS_WAF_CAPTCHA);

  if (isAwsCaptchaEnabled && !isAwsCaptchaConfigured) {
    addBreadcrumb({
      category: 'login.show_captcha',
      message: `Captcha required for ${breadcrumbTag} but awsWafConfig is absent`,
      level: 'warning',
    });
    onInvalidConfig?.();
    return { shouldHandle: false };
  }

  const shouldHandle = isAwsCaptchaEnabled && isAwsCaptchaConfigured && responseStatusCode === 405;

  if (!shouldHandle) {
    return { shouldHandle: false };
  }

  const handler = () => {
    addBreadcrumb({
      category: 'login.show_captcha',
      message: `Captcha shown for ${breadcrumbTag}`,
      level: 'info',
    });
    onShowAwsCaptchaModal({
      onSuccess: () => {
        retry();

        addBreadcrumb({
          category: 'login.show_captcha.puzzle_completed',
          message: `Captcha completed for ${breadcrumbTag}`,
          level: 'info',
        });
      },
    });
    return;
  };

  return { shouldHandle, handler };
};
